import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { message } from 'antd';
import { formatNumber } from '@/lib/utils';
import { imgUrl, proxyImgUrl } from '@/shared/utils';

export default function SingleResult({
  data,
  userCreditInfo,
  isEmailVisible,
  onViewEmail,
}: {
  data: any;
  userCreditInfo?: {
    has_subscription?: boolean;
    expire_time?: string;
  };
  isEmailVisible?: boolean;
  onViewEmail?: () => void;
}) {
  const { t } = useTranslation();

  // View Email 按钮点击处理
  const handleViewEmail = () => {
    // 统一由父组件处理订阅检查和弹窗逻辑
    onViewEmail?.();
  };

  // 复制邮箱到剪贴板
  const handleCopyEmail = () => {
    navigator.clipboard
      .writeText(data.email)
      .then(() => {
        message.success(t('Email copied'));
      })
      .catch(() => {
        message.error(t('Failed to copy email'));
      });
  };

  return (
    <div className="flex flex-col gap-2 p-2 mt-8">
      {/* 用户信息 */}
      <div className="flex items-center gap-2">
        {/* 头像 */}
        <div className="relative size-12 rounded-full border border-[#D9D9D9] overflow-hidden flex-shrink-0">
          <a href={`https://www.instagram.com/${data.username}`} target="_blank" rel="noopener noreferrer">
            <Image
              src={imgUrl(proxyImgUrl(data.avatar), 'small')}
              alt="user avatar"
              width={48}
              height={48}
              className="object-cover"
            />
          </a>
        </div>
        {/* 用户信息内容 */}
        <div className="flex flex-col justify-center flex-1 min-w-0 overflow-hidden">
          {/* 全名和用户名 */}
          <div className="gap-1 mb-1 min-w-0 max-w-full">
            <div className="text-[#0C0C0C] font-bold text-base leading-[1.21em] font-[Inter] truncate block">
              {data.fullName}
            </div>
            <div className="text-[#0C0C0C] font-normal text-base leading-[1.21em] font-[Inter] opacity-60 flex items-center gap-1">
              <a href={`https://www.instagram.com/${data.username}`} target="_blank" rel="noopener noreferrer">
                <span className="truncate">@{data.username}</span>
              </a>
              <Image
                src="/images/account_checked.svg"
                alt="verified"
                width={16}
                height={16}
                className="flex-shrink-0 size-4 object-cover"
              />
            </div>
          </div>
          {/* 粉丝数和订阅数 */}
          <div className="flex items-center gap-2">
            <div className="text-sm text-center font-normal">
              {formatNumber(data.followerCount)} {t('followers')}
            </div>
            <div className="text-sm text-center font-normal">
              {formatNumber(data.followingCount)} {t('followings')}
            </div>
          </div>
        </div>
      </div>
      {/* 匹配分 */}
      <div className="flex items-center justify-start gap-2">
        <div className="flex items-center gap-1">
          <span className={`${data.matchType === 1 ? 'text-[#38C13D]' : 'text-[#FF9500]'} text-base`}>✓</span>
          <span className={`${data.matchType === 1 ? 'text-[#38C13D]' : 'text-[#FF9500]'} font-normal text-sm`}>
            <span className="font-semibold mr-[0.5em]">{data.matchType === 1 ? t('High') : t('Medium')}</span>
            {t('Match')}
          </span>
        </div>
      </div>
      {/* 描述 */}
      <p className="text-[#717182] font-normal text-xs leading-[1.33em] font-[Inter]">{data.description}</p>
      {/* 标签 */}
      <div className="flex flex-wrap items-center gap-1">
        {data.tags.map((tag: string, index: number) => (
          <div key={index} className="px-2 border border-black/10 rounded-[20px]">
            <span className="text-xs font-medium">{tag}</span>
          </div>
        ))}
      </div>
      {/* email按钮 */}
      {isEmailVisible ? (
        // 显示邮箱的白色按钮（点击后复制）
        <button
          onClick={handleCopyEmail}
          className="w-full flex justify-center items-center gap-1.5 bg-white border border-[#D9D9D9] hover:bg-[#F6F5FF] transition text-[#0C0C0C] p-2 mt-2 rounded-lg"
        >
          <span className="text-[#0C0C0C] font-medium text-xs">{data.email}</span>
        </button>
      ) : (
        // View Email 按钮
        <button
          onClick={handleViewEmail}
          className="w-full flex justify-center items-center gap-1.5 bg-[#030213] text-white p-2 mt-2 rounded-lg"
        >
          <Image src="/images/instagram-email-finder/email-icon.svg" alt="email" width={16} height={16} />
          <span className="text-white font-medium text-xs">{t('View Email')}</span>
        </button>
      )}
    </div>
  );
}
